/**
 * ExposureGauge — Proportional risk communication bar.
 *
 * Shows "what the agent could see vs. what it actually used."
 * Color by ratio: green <20%, amber 20-50%, red >50%.
 * Hidden when no exposure data available (graceful degradation).
 */

import type { ExposureSummary } from './action-assurance-types';

const React = (globalThis as any).React;
const { useState, useEffect } = React;

export interface ExposureGaugeProps {
  exposure: ExposureSummary | null | undefined;
  plugin?: string;
  className?: string;
}

function ratioColor(ratio: number): string {
  if (ratio < 20) return 'var(--color-success, #52c41a)';
  if (ratio < 50) return 'var(--color-warning, #faad14)';
  return 'var(--color-error, #ff4d4f)';
}

export function ExposureGauge({ exposure, plugin, className }: ExposureGaugeProps) {
  const [animatedWidth, setAnimatedWidth] = useState(0);

  useEffect(() => {
    if (exposure && exposure.tools_visible_count > 0) {
      const raf = requestAnimationFrame(() => {
        setAnimatedWidth(exposure.exposure_ratio);
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [exposure]);

  if (!exposure || exposure.tools_visible_count === 0) return null;
  if (exposure.tools_visible_count === exposure.tools_used_count) return null;

  const color = ratioColor(exposure.exposure_ratio);
  const testIdPrefix = plugin ? `${plugin}-` : '';

  return React.createElement('div', {
    'data-testid': `${testIdPrefix}exposure-gauge`,
    className,
    style: { padding: '4px 0' },
  },
    React.createElement('div', {
      style: {
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: 11,
        color: 'var(--color-text-secondary)',
        marginBottom: 4,
      },
    },
      React.createElement('span', null,
        `Used ${exposure.tools_used_count} of ${exposure.tools_visible_count} available tools`,
      ),
      React.createElement('span', { style: { color } },
        `${Math.round(exposure.exposure_ratio)}%`,
      ),
    ),
    React.createElement('div', {
      style: {
        height: 6,
        borderRadius: 3,
        backgroundColor: 'var(--color-bg-subtle, rgba(0,0,0,0.06))',
        overflow: 'hidden',
      },
    },
      React.createElement('div', {
        style: {
          height: '100%',
          width: `${animatedWidth}%`,
          backgroundColor: color,
          borderRadius: 3,
          transition: 'width 600ms cubic-bezier(0.34, 1.56, 0.64, 1)',
        },
      }),
    ),
  );
}
